/**
 * Товарный слой ритейла: выборки «товар ↔ полка» и резолв человекочитаемого
 * кода места («R155») в цель маршрута. Схема кодов — буква зоны + цифры с
 * ведущими нулями (docs/core/RETAIL.md): один и тот же код печатается на
 * ценнике, на табличке ряда и показывается в приложении, поэтому сравнение
 * терпимо к регистру и случайным пробелам.
 */
import type { Poi, Product, Venue } from './types'

/** Нормализация кода: регистр и пробелы не значимы («r 155» = «R155»). */
function normCode(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase()
}

/** Код места POI (attrs.code) или null, если код не назначен. */
export function shelfCodeOf(poi: Poi): string | null {
  const code = poi.attrs?.['code']
  return typeof code === 'string' && code.trim() !== '' ? code.trim() : null
}

/** POI по коду места; регистр и пробелы игнорируются. */
export function findByShelfCode(venue: Venue, code: string): Poi | null {
  const needle = normCode(code)
  if (needle === '') return null
  for (const poi of venue.pois) {
    const own = shelfCodeOf(poi)
    if (own !== null && normCode(own) === needle) return poi
  }
  return null
}

/**
 * Резолв ссылки на цель из `?to=`: сперва как id (units/pois/nodes — контракт
 * ADR 0004 неизменен), затем как код места. Так продавец печатает QR
 * `?to=R155`, не зная внутренних id карты.
 */
export function resolveTargetRef(venue: Venue, ref: string): string | null {
  const id = ref.trim()
  if (id === '') return null
  if (
    venue.units.some((u) => u.id === id) ||
    venue.pois.some((p) => p.id === id) ||
    venue.network.nodes.some((n) => n.id === id)
  ) {
    return id
  }
  return findByShelfCode(venue, id)?.id ?? null
}

/** Товары полки в порядке файла (порядок = раскладка сверху вниз в карточке). */
export function productsOnShelf(venue: Venue, shelfId: string): readonly Product[] {
  return (venue.products ?? []).filter((p) => p.shelf === shelfId)
}
